import { Construction } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { NAV_ITEMS } from '@/app/navItems';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { type } from '@/theme/typography';

/**
 * What a §5 navigation item leads to before its screen exists.
 *
 * `DESIGN.md` §5 fixes the sidebar's five items, and this sprint builds one of the screens behind
 * them. The two honest answers to that are "drop the items" and "say so on arrival"; §5 settles the
 * first — the list is the design — so this is the second. **The answer it replaces was React
 * Router's own developer error page** ("Unexpected Application Error! 404 Not Found · 💿 Hey
 * developer 👋"), which every Admin and Recruiter reached in three clicks, outside the shell, with
 * no way back: precisely what §8 forbids ("never a raw status code, never a stack trace").
 *
 * It renders **inside the shell**, so the sidebar is still there and the user is one click from a
 * screen that works — a placeholder that takes the navigation away with it is the error page again
 * with better manners.
 */
export default function NotBuilt() {
  const { t } = useTranslation(['common', 'navigation']);
  const location = useLocation();
  const navigate = useNavigate();

  // The screen's own name, from the same table the sidebar reads, so the two cannot disagree.
  const item = NAV_ITEMS.find((one) => one.to === location.pathname);
  const title = item ? t(`navigation:${item.labelKey}`) : t('common:notBuiltTitle');

  return (
    <>
      <h1 className={type.pageTitle}>{title}</h1>
      <EmptyState
        className="mt-10"
        icon={Construction}
        message={t('common:notBuilt')}
        action={
          <Button variant="secondary" onClick={() => void navigate('/')}>
            {t('common:backToDashboard')}
          </Button>
        }
      />
    </>
  );
}
